// Reference-PDF upload/delete mutation hooks shared by the admin PDF
// controls (ModulePdfUpload, ReferencePdfUpload). The three-step
// presign → S3 PUT → confirm flow was hand-rolled and divergent in each
// component; this centralises it and the S3 PUT.
//
// Kept in its own module rather than useOrgTemplates so the many admin
// tests that vi.mock("../../hooks/useOrgTemplates") with an explicit
// factory don't have to stub these too.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { orgTemplatesApi } from "../services/api";
import { orgTemplateKeys } from "./orgTemplateKeys";

async function putPdfToS3(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": "application/pdf" },
  });
  if (!res.ok) {
    throw new Error("Failed to upload file to S3");
  }
}

/** Upload a per-module reference PDF (or the contract-details PDF when
 *  isContractDetails is true). */
export function useUploadModulePdf(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      moduleId,
      file,
      isContractDetails,
    }: {
      moduleId: string;
      file: File;
      isContractDetails: boolean;
    }) => {
      const { uploadUrl, key } = isContractDetails
        ? await orgTemplatesApi.getContractDetailsPdfUploadUrl(templateId)
        : await orgTemplatesApi.getModulePdfUploadUrl(templateId, moduleId);
      await putPdfToS3(uploadUrl, file);
      if (isContractDetails) {
        await orgTemplatesApi.confirmContractDetailsPdf(templateId, key);
      } else {
        await orgTemplatesApi.confirmModulePdf(templateId, moduleId, key);
      }
      return key;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orgTemplateKeys.detail(templateId),
      });
      // The presigned-URL queries live under their own roots with a
      // 30-minute staleTime; without an explicit drop a replaced or
      // deleted PDF keeps being served from the stale URL.
      queryClient.invalidateQueries({
        queryKey: orgTemplateKeys.modulePdfRoot(templateId),
      });
      queryClient.invalidateQueries({
        queryKey: orgTemplateKeys.contractDetailsPdf(templateId),
      });
    },
  });
}

export function useDeleteModulePdf(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      moduleId,
      isContractDetails,
    }: {
      moduleId: string;
      isContractDetails: boolean;
    }) => {
      if (isContractDetails) {
        await orgTemplatesApi.deleteContractDetailsPdf(templateId);
      } else {
        await orgTemplatesApi.deleteModulePdf(templateId, moduleId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orgTemplateKeys.detail(templateId),
      });
      // The presigned-URL queries live under their own roots with a
      // 30-minute staleTime; without an explicit drop a replaced or
      // deleted PDF keeps being served from the stale URL.
      queryClient.invalidateQueries({
        queryKey: orgTemplateKeys.modulePdfRoot(templateId),
      });
      queryClient.invalidateQueries({
        queryKey: orgTemplateKeys.contractDetailsPdf(templateId),
      });
    },
  });
}

/** Upload the single whole-template reference PDF (single-PDF mode). */
export function useUploadReferencePdf(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const { uploadUrl, key } =
        await orgTemplatesApi.getReferencePdfUploadUrl(templateId);
      await putPdfToS3(uploadUrl, file);
      await orgTemplatesApi.confirmReferencePdf(templateId, key);
      return key;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orgTemplateKeys.detail(templateId),
      });
      // The presigned-URL queries live under their own roots with a
      // 30-minute staleTime; without an explicit drop a replaced or
      // deleted PDF keeps being served from the stale URL.
      queryClient.invalidateQueries({
        queryKey: orgTemplateKeys.modulePdfRoot(templateId),
      });
      queryClient.invalidateQueries({
        queryKey: orgTemplateKeys.contractDetailsPdf(templateId),
      });
    },
  });
}

export function useDeleteReferencePdf(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => orgTemplatesApi.deleteReferencePdf(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orgTemplateKeys.detail(templateId),
      });
      // The presigned-URL queries live under their own roots with a
      // 30-minute staleTime; without an explicit drop a replaced or
      // deleted PDF keeps being served from the stale URL.
      queryClient.invalidateQueries({
        queryKey: orgTemplateKeys.modulePdfRoot(templateId),
      });
      queryClient.invalidateQueries({
        queryKey: orgTemplateKeys.contractDetailsPdf(templateId),
      });
    },
  });
}
